import { FuturesMarginType } from '@kwenta/sdk/types'
import { createSelector } from '@reduxjs/toolkit'
import { isAddress } from 'ethers/lib/utils.js'

import { selectFuturesType } from 'state/futures/common/selectors'
import { RootState } from 'state/store'
import { FetchStatus } from 'state/types'
import { selectIsTestnet, selectWallet } from 'state/wallet/selectors'

import { unserializeLeaderboard } from './utils'

export const selectSelectedTimeframe = (state: RootState) => state.stats.selectedTimeframe

export const selectMinTimestamp = createSelector(
	(state: RootState) => state.stats.selectedTimeframe,
	(selectedTimeframe) => {
		if (selectedTimeframe === 'MAX') return 0

		const timeframeSeconds =
			selectedTimeframe === '1M'
				? 60 * 60 * 24 * 30
				: selectedTimeframe === '1Y'
				? 60 * 60 * 24 * 365
				: 0
		const currentTimestamp = new Date().getTime() / 1000
		return Math.floor(currentTimestamp - timeframeSeconds)
	}
)

export const selectLeaderboardSearchTerm = (state: RootState) => state.stats.leaderboardSearchTerm

export const selectValidSearchTerm = createSelector(selectLeaderboardSearchTerm, (searchTerm) => {
	return searchTerm && (isAddress(searchTerm) || searchTerm.endsWith('.eth'))
		? searchTerm
		: undefined
})

export const selectV3Leaderboard = createSelector(
	selectLeaderboardSearchTerm,
	selectWallet,
	(state: RootState) => state.stats.leaderboard.v3,
	(searchTerm, wallet, leaderboard) => {
		const leaderboardItems = unserializeLeaderboard(leaderboard)
		const filtered = leaderboardItems.all.filter((item) =>
			item.trader.toLowerCase().includes(searchTerm.toLowerCase())
		)
		const walletItems = leaderboardItems.all.filter((item) =>
			item.trader.toLowerCase().includes(wallet?.toLowerCase() ?? '')
		)

		// TODO: Refactor leaderboard to support full data rather than bottom / top
		return {
			...leaderboardItems,
			search: filtered,
			wallet: walletItems,
		}
	}
)

export const selectV2Leaderboard = createSelector(
	(state: RootState) => state.stats.leaderboard.v2,
	(leaderboard) => {
		return unserializeLeaderboard(leaderboard)
	}
)

export const selectLeaderboard = createSelector(
	selectV2Leaderboard,
	selectV3Leaderboard,
	selectFuturesType,
	(v2, v3, type) => {
		return type === FuturesMarginType.CROSS_MARGIN ? v3 : v2
	}
)

export const selectLeaderboardLoading = createSelector(
	selectIsTestnet,
	(state: RootState) => state.stats.queryStatuses,
	(isTestnet, queryStatuses) => {
		return isTestnet
			? queryStatuses.leaderboardV3.status === FetchStatus.Loading
			: queryStatuses.leaderboardV2.status === FetchStatus.Loading
	}
)
